<template lang="pug">
div
    feathers-vuex-find(service="users" :query="dataQuery" :fetch-query="fetchQuery" watch="query")
        v-card(slot-scope="{ items: users, pagination: serverPagination, isFindPending }")
            v-card-title User Token Management
                v-spacer
                v-text-field(
                    v-model="searchInput"
                    append-icon="mdi-magnify"
                    label="Search users by name or email (Press Enter)"
                    single-line
                    hide-details
                    clearable
                    @keyup.enter="performSearch"
                    @click:append="performSearch"
                    @click:clear="clearSearch"
                    style="max-width: 400px"
                )
            
            v-data-table(
                :headers="headers"
                :items="isFindPending ? [] : users"
                :loading="isFindPending"
                loading-text="Loading users..."
                :options.sync="pagination"
                :server-items-length="!serverPagination ? 0 : serverPagination.mostRecent.total"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100], showCurrentPage: true }"
            )
                template(v-slot:item.fname="{ item }")
                    span {{ item.fname }} {{ item.lname }}

                template(v-slot:item.current_tokens="{ item }")
                    v-chip(small color="success" dark) 
                        v-icon(left small) mdi-coin
                        | {{ getTokenBalance(item.id, 'current') }}

                template(v-slot:item.lifetime_tokens="{ item }")
                    span {{ getTokenBalance(item.id, 'lifetime') }}

                template(v-slot:item.actions="{ item }")
                    v-btn(icon small color="primary" @click="openAddTokenDialog(item)" title="Add Tokens")
                        v-icon mdi-plus-circle
                    v-btn(icon small color="info" @click="viewHistory(item)" title="View History")
                        v-icon mdi-history

    // Add Token Dialog
    v-dialog(v-model="addTokenDialog" max-width="500px" persistent)
        v-card
            v-card-title 
                span.headline Add Tokens to User
            v-card-text
                v-form(ref="addTokenForm" v-model="validAddToken")
                    v-alert(type="info" dense outlined)
                        | Adding tokens to: 
                        strong {{ selectedUser.fname }} {{ selectedUser.lname }}
                        br
                        | Current Balance: 
                        strong {{ formatNumber(selectedUser.current_tokens || 0) }} tokens

                    v-text-field(
                        v-model.number="tokenAmount"
                        label="Token Amount*"
                        type="number"
                        :rules="[rules.required, rules.positive]"
                        hint="Number of tokens to add"
                        persistent-hint
                        prefix="+"
                    )

                    v-textarea(
                        v-model="tokenReason"
                        label="Reason*"
                        :rules="[rules.required]"
                        hint="Explain why these tokens are being added"
                        persistent-hint
                        rows="3"
                    )

                    v-alert(v-if="tokenAmount > 0" type="success" dense outlined)
                        | New Balance: 
                        strong {{ formatNumber((selectedUser.current_tokens || 0) + tokenAmount) }} tokens

            v-card-actions
                v-spacer
                v-btn(text @click="closeAddTokenDialog") Cancel
                v-btn(
                    color="primary" 
                    :disabled="!validAddToken || processing"
                    :loading="processing"
                    @click="addTokens"
                ) 
                    v-icon(left) mdi-plus
                    | Add Tokens

    // History Dialog
    v-dialog(v-model="historyDialog" max-width="800px")
        v-card
            v-card-title 
                span.headline Token History
                v-spacer
                v-btn(icon @click="historyDialog = false")
                    v-icon mdi-close
            v-card-text
                v-alert(type="info" dense outlined)
                    | User: 
                    strong {{ selectedUser.fname }} {{ selectedUser.lname }}
                    br
                    | Current Balance: 
                    strong {{ formatNumber(selectedUser.current_tokens || 0) }} tokens

                v-data-table(
                    :headers="historyHeaders"
                    :items="tokenHistory"
                    :loading="loadingHistory"
                    loading-text="Loading history..."
                    :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
                )
                    template(v-slot:item.type="{ item }")
                        v-chip(small :color="getTypeColor(item.type)" dark) {{ item.type }}

                    template(v-slot:item.amount="{ item }")
                        span(:class="getAmountClass(item.type)") {{ formatAmount(item) }}

                    template(v-slot:item.date_added="{ item }")
                        span {{ formatDate(item.date_added) }}
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import feathersClient from '@/plugins/feathers-client';

export default {
    name: 'UserTokenManager',
    
    data() {
        return {
            search: '',
            searchInput: '',
            processing: false,
            addTokenDialog: false,
            historyDialog: false,
            validAddToken: false,
            selectedUser: {},
            tokenAmount: 0,
            tokenReason: '',
            tokenHistory: [],
            loadingHistory: false,
            tokenBalances: {}, // Store token balances by user ID
            
            pagination: {
                page: 1,
                itemsPerPage: 25,
                sortBy: ['id'],
                sortDesc: [true]
            },
            
            headers: [
                { text: 'ID', value: 'id', width: 80 },
                { text: 'Name', value: 'fname', width: 200 },
                { text: 'Email', value: 'email', width: 250 },
                { text: 'Current Tokens', value: 'current_tokens', width: 150, align: 'center', sortable: false },
                { text: 'Lifetime Tokens', value: 'lifetime_tokens', width: 150, align: 'center', sortable: false },
                { text: 'Actions', value: 'actions', sortable: false, width: 120, align: 'center' }
            ],
            
            historyHeaders: [
                { text: 'Date', value: 'date_added', width: 180 },
                { text: 'Type', value: 'type', width: 150 },
                { text: 'Amount', value: 'amount', width: 120, align: 'right' },
                { text: 'Balance After', value: 'current_balance', width: 150, align: 'right' },
                { text: 'Comment', value: 'comment', width: 300 }
            ],
            
            rules: {
                required: v => !!v || 'This field is required',
                positive: v => v > 0 || 'Amount must be greater than 0'
            }
        };
    },
    
    computed: {
        ...mapGetters('auth', ['user']),
        
        searchQuery() {
            if (this.search) {
                if (isNaN(parseInt(this.search))) {
                    return {
                        $or: [
                            { fname: { $like: `%${this.search}%` } },
                            { lname: { $like: `%${this.search}%` } },
                            { email: { $like: `%${this.search}%` } }
                        ]
                    };
                } else {
                    return {
                        id: parseInt(this.search)
                    };
                }
            }
            return null;
        },
        
        searchDataQuery() {
            if (this.search) {
                if (isNaN(parseInt(this.search))) {
                    return {
                        $or: [
                            { fname: { $regex: new RegExp(`.*${this.escapeRegex(this.search)}.*`, 'gi') } },
                            { lname: { $regex: new RegExp(`.*${this.escapeRegex(this.search)}.*`, 'gi') } },
                            { email: { $regex: new RegExp(`.*${this.escapeRegex(this.search)}.*`, 'gi') } }
                        ]
                    };
                } else {
                    return {
                        id: parseInt(this.search)
                    };
                }
            }
            return null;
        },
        
        sortingQuery() {
            if (this.pagination.sortBy.length !== 0) {
                const $sort = {};
                this.pagination.sortBy.forEach((item, index) => {
                    $sort[item] = this.pagination.sortDesc[index] ? -1 : 1;
                });
                return { $sort };
            }
            return null;
        },
        
        dataQuery() {
            return {
                status: 1, // Active users only
                ...this.searchDataQuery,
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page - 1) * this.pagination.itemsPerPage,
                ...this.sortingQuery
            };
        },
        
        fetchQuery() {
            return {
                status: 1,
                ...this.searchQuery,
                $limit: this.pagination.itemsPerPage,
                $skip: (this.pagination.page - 1) * this.pagination.itemsPerPage,
                ...this.sortingQuery
            };
        }
    },
    
    methods: {
        async fetchTokenBalance(userId) {
            if (!this.tokenBalances[userId]) {
                try {
                    const tokenInfo = await feathersClient.service('utilities/get-current-token').get(userId);
                    this.$set(this.tokenBalances, userId, {
                        current: tokenInfo.token || 0,
                        lifetime: tokenInfo.lifetime || 0
                    });
                } catch (error) {
                    console.error(`Error fetching tokens for user ${userId}:`, error.message);
                    this.$set(this.tokenBalances, userId, { current: 0, lifetime: 0 });
                }
            }
        },
        
        getTokenBalance(userId, type) {
            // Fetch token balance asynchronously if not cached
            if (!this.tokenBalances[userId]) {
                this.fetchTokenBalance(userId);
                return '...';
            }
            return this.formatNumber(this.tokenBalances[userId][type] || 0);
        },
        
        escapeRegex(string) {
            return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        },
        
        performSearch() {
            this.search = this.searchInput;
        },
        
        clearSearch() {
            this.searchInput = '';
            this.search = '';
        },
        
        openAddTokenDialog(user) {
            this.selectedUser = { ...user };
            this.tokenAmount = 0;
            this.tokenReason = '';
            this.addTokenDialog = true;
        },
        
        closeAddTokenDialog() {
            this.addTokenDialog = false;
            this.selectedUser = {};
            this.tokenAmount = 0;
            this.tokenReason = '';
            if (this.$refs.addTokenForm) {
                this.$refs.addTokenForm.reset();
            }
        },
        
        async addTokens() {
            if (!this.$refs.addTokenForm.validate()) {
                return;
            }
            
            this.processing = true;
            try {
                const userId = this.selectedUser.id;
                const userName = `${this.selectedUser.fname} ${this.selectedUser.lname}`;
                const tokenAmount = this.tokenAmount;
                
                // Create a manual token addition record in bh-redeem
                await feathersClient.service('bh-redeem').create({
                    user_id: userId,
                    amount: tokenAmount,
                    type: 'GIFT_CARD', // Using GIFT_CARD type for manual additions
                    comment: `Manual Token Addition: ${this.tokenReason} (Added by: ${this.user.email})`,
                    status: '1'
                });
                
                // Clear token balance cache for this user so it refreshes
                if (this.tokenBalances[userId]) {
                    this.$delete(this.tokenBalances, userId);
                }
                
                // Close dialog first
                this.closeAddTokenDialog();
                
                // Show success message after dialog is closed
                this.$toast.success(`Successfully added ${tokenAmount} tokens to ${userName}`);
            } catch (error) {
                console.error('Error adding tokens:', error);
                this.$toast.error('Failed to add tokens: ' + (error.message || 'Unknown error'));
            } finally {
                this.processing = false;
            }
        },
        
        async viewHistory(user) {
            this.selectedUser = { ...user };
            this.historyDialog = true;
            this.loadingHistory = true;
            
            try {
                // Fetch redemption history
                const history = await feathersClient.service('bh-redeem').find({
                    query: {
                        user_id: user.id,
                        $sort: { date_added: -1 },
                        $limit: 100
                    }
                });
                
                this.tokenHistory = history.data || history;
            } catch (error) {
                console.error('Error loading history:', error);
                this.$toast.error('Failed to load token history');
            } finally {
                this.loadingHistory = false;
            }
        },
        
        formatNumber(num) {
            return new Intl.NumberFormat().format(num);
        },
        
        formatAmount(item) {
            const amount = item.type === 'REDEEM' 
                ? `-${this.formatNumber(this.getRedeemAmount(item.amount))}`
                : `+${this.formatNumber(item.amount)}`;
            return amount;
        },
        
        getRedeemAmount(amount) {
            const conversion = {
                50: 5000,
                100: 10000,
                200: 25000,
                500: 50000
            };
            return conversion[amount] || amount;
        },
        
        getAmountClass(type) {
            return type === 'REDEEM' ? 'red--text' : 'green--text';
        },
        
        getTypeColor(type) {
            switch(type) {
                case 'REDEEM': return 'error';
                case 'GIFT_CARD': return 'success';
                default: return 'grey';
            }
        },
        
        formatDate(date) {
            return moment(date).format('YYYY-MM-DD HH:mm:ss');
        }
    }
};
</script>

<style scoped>
.v-chip {
    font-weight: 600;
}

.red--text {
    font-weight: 600;
}

.green--text {
    font-weight: 600;
}
</style>
